"use client";

import type { IHeader7Widget } from "@/types/page";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { Phone } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useLayoutEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

export type Header7WidgetItem = {
  label: string;
  value: string;
  link?: string;
};

const DEFAULT_BAR_BG = "#291973";
const DEFAULT_PILL_BG = "#ffffff1a";
const DEFAULT_WHATSAPP_BG = "#4CAF51";

/** Inline WhatsApp glyph used when no custom icon is uploaded. */
function WhatsAppGlyph({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path d="M17.5 14.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z" />
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.86 9.86 0 0 0 4.78 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.84 9.84 0 0 0 12.04 2zm0 18.13h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.69 8.24-8.24 8.24z" />
    </svg>
  );
}

function normalizeMetadataInput(raw: unknown): IHeader7Widget[] | undefined {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) return raw as IHeader7Widget[];
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? (parsed as IHeader7Widget[]) : undefined;
    } catch {
      return undefined;
    }
  }
  // MySQL JSON / drivers sometimes deserialize array-like objects { "0": row, "1": row }
  if (typeof raw === "object" && raw !== null) {
    const keys = Object.keys(raw);
    if (keys.length && keys.every((k) => /^\d+$/.test(k))) {
      return Object.values(raw) as IHeader7Widget[];
    }
  }
  return undefined;
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim();
}

function widgetsFromMetadata(
  metadata: IHeader7Widget[] | undefined | null,
): Header7WidgetItem[] {
  if (!metadata?.length) return [];

  return [...metadata]
    .filter((m) => m.status !== false)
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .map((m) => {
      const raw = m as IHeader7Widget & Record<string, unknown>;
      return {
        label: str(raw.widget_label ?? raw.widgetLabel),
        value: str(raw.widget_value ?? raw.widgetValue),
        link: str(raw.widget_link ?? raw.widgetLink) || undefined,
      };
    })
    .filter((m) => m.label || m.value);
}

function pickBoolFlag(v: unknown, fallback = false): boolean {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(s)) return true;
    if (["false", "0", "no", "off"].includes(s)) return false;
  }
  return fallback;
}

function shouldUnoptimizeImageSrc(src: string): boolean {
  return /^https?:\/\//i.test(src.trim());
}

/** Build a WhatsApp deep link from a raw number when no explicit link is set. */
function buildWhatsAppLink(link: string, number: string): string {
  if (link) return link;
  const digits = number.replace(/[^\d]/g, "");
  if (!digits) return "";
  return `https://api.whatsapp.com/send/?phone=${digits}&text&type=phone_number&app_absent=0`;
}

function Header7({ data }: { data: any }) {
  const sectionData = data?.data ?? data;

  if (!sectionData) {
    return null;
  }

  const raw = sectionData as Record<string, unknown>;

  const {
    background_color = DEFAULT_BAR_BG,
    pill_background_color,
    pill_chip_background_colour,
    label_text_color = "#FFFFFF",
    phone_text_color = "#FFFFFF",
    metadata,
    show_on_mobile = false,
    show_logo = false,
    logo_image,
    whatsapp_show = true,
    whatsapp_number = "",
    whatsapp_link = "",
    whatsapp_button_text = "WhatsApp Chat",
    whatsapp_button_color = DEFAULT_WHATSAPP_BG,
    whatsapp_button_text_color = "#FFFFFF",
    whatsapp_icon,
    button_name = "",
    button_link = "",
    button_color = "",
    button_text_color = "",
  } = sectionData;

  const logoSrc = str(logo_image);
  const waIconSrc = str(whatsapp_icon);

  const stickyToTop = pickBoolFlag(raw.sticky_to_top ?? raw.stickyToTop);
  const rootRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const openApplyLeadPopup = useCallback(() => {
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [dispatch]);

  useLayoutEffect(() => {
    if (!stickyToTop) {
      document.documentElement.style.removeProperty("--header4-sticky-height");
      return;
    }
    const el = rootRef.current;
    if (!el) return;
    const setVar = () => {
      document.documentElement.style.setProperty(
        "--header4-sticky-height",
        `${el.offsetHeight}px`,
      );
    };
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--header4-sticky-height");
    };
  }, [stickyToTop]);

  const pillBg =
    str(pill_chip_background_colour) || str(pill_background_color) || DEFAULT_PILL_BG;
  const barBg = str(background_color) || DEFAULT_BAR_BG;

  const widgets = widgetsFromMetadata(normalizeMetadataInput(metadata));

  const showLogo = pickBoolFlag(show_logo) && !!logoSrc;
  const waLink = buildWhatsAppLink(str(whatsapp_link), str(whatsapp_number));
  const showWhatsApp = pickBoolFlag(whatsapp_show, true) && !!waLink;
  const waText = str(whatsapp_button_text) || "WhatsApp Chat";
  const ctaText = str(button_name);
  const ctaLink = str(button_link);
  const hasCtaLink = !!ctaLink && ctaLink !== "#";
  // Colors default to empty in the CMS; fall back to white-on-bar styling.
  const ctaBg = str(button_color) || "#FFFFFF";
  const ctaTextColor = str(button_text_color) || DEFAULT_BAR_BG;
  const showOnMobile = pickBoolFlag(show_on_mobile);
  // Mobile default: the bar keeps only logo + CTA; contact pills and the
  // WhatsApp pill move to floating bottom-left icons. show_on_mobile forces
  // the full bar on mobile instead (no floating icons).
  const showMobileBar = showOnMobile || showLogo || !!ctaText;
  const phoneFloatLink = widgets.find((w) => w.link)?.link ?? "";
  const showFloatingIcons = !showOnMobile && (showWhatsApp || !!phoneFloatLink);

  return (
    <>
    <div
      ref={rootRef}
      className={cn(
        "w-full items-center gap-4 px-4 py-2.5 min-[1024px]:px-9",
        showLogo ? "justify-between" : "justify-end",
        showMobileBar ? "flex" : "hidden min-[1024px]:flex",
        "shadow-sm",
        stickyToTop && "sticky top-0 z-[100]",
      )}
      style={{ backgroundColor: barBg }}
    >
      {showLogo ? (
        <div className="flex shrink-0 items-center">
          <Image
            src={logoSrc}
            alt="logo"
            width={132}
            height={30}
            className="h-auto w-auto max-h-8 object-contain"
            unoptimized={shouldUnoptimizeImageSrc(logoSrc)}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {widgets.map((item, index) => {
          const content = (
            <>
              <Phone
                aria-hidden
                className="size-4 shrink-0"
                style={{ color: phone_text_color, fill: phone_text_color }}
                strokeWidth={0.5}
              />
              {item.label ? (
                <span
                  className="text-xs font-normal"
                  style={{ color: label_text_color }}
                >
                  {item.label}
                </span>
              ) : null}
              {item.value ? (
                <span
                  className="text-xs font-semibold"
                  style={{ color: phone_text_color }}
                >
                  {item.value}
                </span>
              ) : null}
            </>
          );

          const pillClass = cn(
            "items-center gap-2 rounded-md px-4 py-2",
            showOnMobile ? "flex" : "hidden min-[1024px]:flex",
          );

          return item.link ? (
            <Link
              key={`${item.label}-${index}`}
              href={item.link}
              className={pillClass}
              style={{ backgroundColor: pillBg }}
            >
              {content}
            </Link>
          ) : (
            <div
              key={`${item.label}-${index}`}
              className={pillClass}
              style={{ backgroundColor: pillBg }}
            >
              {content}
            </div>
          );
        })}

        {showWhatsApp ? (
          <Link
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "shrink-0 items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm",
              showOnMobile ? "flex" : "hidden min-[1024px]:flex",
            )}
            style={{
              backgroundColor: str(whatsapp_button_color) || DEFAULT_WHATSAPP_BG,
              color: str(whatsapp_button_text_color) || "#FFFFFF",
            }}
          >
            {waIconSrc ? (
              <Image
                src={waIconSrc}
                alt={waText || "WhatsApp"}
                width={20}
                height={20}
                className="size-5 shrink-0 object-contain"
                unoptimized={shouldUnoptimizeImageSrc(waIconSrc)}
              />
            ) : (
              <WhatsAppGlyph className="size-5 shrink-0" />
            )}
            <span>{waText}</span>
          </Link>
        ) : null}

        {ctaText ? (
          (() => {
            const ctaClass =
              "flex shrink-0 cursor-pointer items-center rounded-md px-4 py-1.5 text-[16px] font-semibold shadow-sm";
            const ctaStyle = { backgroundColor: ctaBg, color: ctaTextColor };
            // No link configured (or a placeholder "#") falls back to the
            // register lead popup so the CTA is never a dead click.
            return hasCtaLink ? (
              <Link href={ctaLink} className={ctaClass} style={ctaStyle}>
                {ctaText}
              </Link>
            ) : (
              <button
                type="button"
                onClick={openApplyLeadPopup}
                className={ctaClass}
                style={ctaStyle}
              >
                {ctaText}
              </button>
            );
          })()
        ) : null}
      </div>
    </div>

    {/* `data-mobile-float-dock` lets FloatingIcons stack the promotional icon
        directly above this column instead of over it. */}
    {showFloatingIcons ? (
      <div
        data-mobile-float-dock
        className="fixed bottom-6 left-4 z-[90] flex flex-col gap-3 min-[1024px]:hidden"
      >
        {showWhatsApp ? (
          <Link
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={waText}
            className="flex size-12 items-center justify-center rounded-full shadow-lg"
            style={{
              backgroundColor: str(whatsapp_button_color) || DEFAULT_WHATSAPP_BG,
              color: str(whatsapp_button_text_color) || "#FFFFFF",
            }}
          >
            {waIconSrc ? (
              <Image
                src={waIconSrc}
                alt={waText || "WhatsApp"}
                width={28}
                height={28}
                className="size-7 shrink-0 object-contain"
                unoptimized={shouldUnoptimizeImageSrc(waIconSrc)}
              />
            ) : (
              <WhatsAppGlyph className="size-7 shrink-0" />
            )}
          </Link>
        ) : null}
        {phoneFloatLink ? (
          <Link
            href={phoneFloatLink}
            aria-label="Call"
            className="flex size-12 items-center justify-center rounded-full shadow-lg"
            style={{ backgroundColor: "#272323" }}
          >
            <Phone
              aria-hidden
              className="size-6 shrink-0"
              style={{ color: "#FFFFFF", fill: "#FFFFFF" }}
              strokeWidth={0.5}
            />
          </Link>
        ) : null}
      </div>
    ) : null}
    </>
  );
}

export default Header7;
